/* =============================================================================
 * FICHA DE CADA UNA DE LAS SEIS RUTAS · titulo, descripcion y canonica.
 * -----------------------------------------------------------------------------
 * NADA de aqui se teclea a mano: todo sale de `features/events`, que ya esta
 * tipado y validado por Zod. Si manana cambia una fecha en ADR-003, cambia el
 * dato, cambia la descripcion y cambia la vista previa de WhatsApp a la vez.
 * Es imposible que se descuadren.
 *
 * ESTE FICHERO ES LIGERO A PROPOSITO. Lo importa el cliente (`RouteHead`), asi
 * que NO puede arrastrar el manifiesto de imagenes ni los constructores de
 * JSON-LD: eso vive en `head.ts`, que solo corre en el build. Si algun dia
 * alguien importa `./head` desde un componente, se lleva 40 KB de datos al
 * bundle sin necesidad.
 *
 * Importaciones RELATIVAS obligatorias: este modulo lo bundlea esbuild dentro
 * de `vite.config.ts`, donde el alias `@/` todavia no existe.
 * ========================================================================== */
import { EVENTS } from '../events/event.config';
import { formatDateRangeLong } from '../events/event.datetime';
import { TOUR_HUB_PATH } from '../events/event.links';
import type { TourEvent } from '../events/event.types';
import { absoluteUrl, PERFORMER_NAME, SITE_NAME } from './site';

export interface RouteSeo {
  /** Ruta interna tal cual la sirve el router ('/', '/bogota'). */
  readonly path: string;
  /** URL absoluta para `<link rel="canonical">` y `og:url`. */
  readonly canonical: string;
  /** `<title>`, `og:title` y `twitter:title`. Distinto en las seis. */
  readonly title: string;
  /** `<meta name="description">`, `og:description` y `twitter:description`. */
  readonly description: string;
  /** El destino, o `null` en el hub. Lo usa `head.ts` para el JSON-LD. */
  readonly event: TourEvent | null;
}

/**
 * Descripcion de un destino: persona + ciudad + fechas reales + la promesa que
 * ya estaba aprobada. Ni un dato nuevo, y las cinco caben en los ~160
 * caracteres que Google recorta.
 */
function describeEvent(event: TourEvent): string {
  // Cancun es el unico tagline que no acaba en punto ("... · Comité íntimo").
  // Se cierra la frase aqui en vez de retocar el copy aprobado de la config.
  const promesa = /[.!?]$/.test(event.tagline) ? event.tagline : `${event.tagline}.`;
  return `${PERFORMER_NAME} en ${event.city} · ${formatDateRangeLong(event)}. ${promesa}`;
}

/**
 * Descripcion del hub. El texto es el que ya estaba en `index.html` y venia
 * aprobado; lo unico que cambia es que la lista de ciudades se DERIVA de la
 * config en vez de estar tecleada, para que no pueda quedarse vieja.
 */
function describeHub(): string {
  const cities = EVENTS.map((e) => e.city);
  const last = cities[cities.length - 1];
  const listed = `${cities.slice(0, -1).join(', ')} y ${last ?? ''}`;
  return (
    `Gira 2027 «Nueva Humanidad Galáctica» de ${PERFORMER_NAME}: ` +
    `${String(EVENTS.length)} destinos, un viaje de transformación. ${listed}.`
  );
}

const HUB_ROUTE: RouteSeo = {
  path: TOUR_HUB_PATH,
  canonical: absoluteUrl(TOUR_HUB_PATH),
  title: `${SITE_NAME} · ${PERFORMER_NAME}`,
  description: describeHub(),
  event: null,
};

const EVENT_ROUTES: readonly RouteSeo[] = EVENTS.map((event) => ({
  path: event.path,
  canonical: absoluteUrl(event.path),
  // "Bogotá · Sanación de la Consciencia y Frecuencia Origen". La ciudad
  // PRIMERO: en una vista previa de WhatsApp el titulo se corta por la derecha,
  // y lo que nunca puede perderse es de que ciudad habla.
  title: `${event.city} · ${event.title}`,
  description: describeEvent(event),
  event,
}));

/** Las SEIS rutas indexables, en orden: hub y luego la gira cronologica. */
export const SEO_ROUTES: readonly RouteSeo[] = [HUB_ROUTE, ...EVENT_ROUTES];

/**
 * Ficha de una ruta, o `undefined` si esa direccion no es una de las seis
 * (la 404 no tiene ficha y no debe fingir que la tiene).
 */
export function getRouteSeo(path: string): RouteSeo | undefined {
  // Se tolera la barra final ('/bogota/'), que es como nginx sirve el HTML
  // emitido por directorio; la canonica que se declara sigue siendo sin barra.
  const clean = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  return SEO_ROUTES.find((route) => route.path === clean);
}
